import React, { useEffect, useState } from "react";
import {BannerContextProvider} from "../contexts/BannerContext";
import {AppStateContextProvider} from "../contexts/AppStateContext";
import { WebSocketProvider } from "../contexts/WebSocketContext";
import {ThemeProvider} from "../contexts/ThemeContext";
import {TradingContextProvider} from "../contexts/TradingContext";
import {StreamsContextProvider} from "../contexts/StreamsContext";
import {StdDevProvider} from "../contexts/StdDevContext";
import {MaintenanceProvider} from "../contexts/MaintenanceContext";
import {TradeConfirmationProvider} from "../contexts/TradeConfirmationContext";
import {JournalModalProvider} from "../contexts/JournalModalContext";
import {OrderUndoProvider} from "../contexts/OrderUndoContext";
import { EnforcementAlertsProvider } from "../contexts/EnforcementAlertsContext";
import { TradingRulesConfigProvider } from "../contexts/TradingRulesConfigContext";
import { TradingHoursRestrictionProvider } from "../contexts/TradingHoursRestrictionContext";
import { TradeChecklistProvider } from "../contexts/TradeChecklistContext";
import { BacktestProvider } from "../contexts/BacktestContext";
import { Outlet, useLocation } from 'react-router-dom';
import {PusherProvider} from "../providers/PusherProvider";
import NotificationStack from "../components/shared/NotificationStack";
import {PositionCloseProvider} from "../contexts/PositionCloseContext";
import MaintenanceGate from "../components/MaintenanceGate";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import StateInspector from "../components/dev/StateInspector";
import AutoCloseConfirmModal from "../components/AutoCloseConfirmModal";
import PriceAlertListener from "../components/PriceAlertListener";
import PositionLossListener from "../components/PositionLossListener";
import AiOrderDraftListener from "../components/AiOrderDraftListener";
import ChartsWebSocketHandler from "../components/ChartsWebSocketHandler";
import TradingWebSocketHandler from "../components/TradingWebSocketHandler";
import QuotesWebSocketHandler from "../components/QuotesWebSocketHandler";
import ConnectTradeCallbackHandler from "../components/broker/ConnectTradeCallbackHandler";
import StreamConnectionMonitor from "../components/StreamConnectionMonitor";
import TradeStationReferralPromoBar, {
  TradeStationReferralPromoFooter,
} from "../components/shared/TradeStationReferralPromoBar";
import PendingDailyLossRuleApplier from "../components/onboarding/PendingDailyLossRuleApplier";
import ImpersonationBanner from "../components/ImpersonationBanner";
import InstallAppBanner from "../components/shared/InstallAppBanner";

const BannerWrapper = () => {
  return (
    <>
      <NotificationStack />
      <Outlet/>
    </>
  );
};



export const Environment = () => {
  const location = useLocation();

  const [showInspector, setShowInspector] = useState(() => {
    try {
      if (typeof window !== 'undefined') {
        return new URLSearchParams(window.location.search).get('state') === '1';
      }
    } catch (_) {}
    return false;
  });

  useEffect(() => {
    const onToggle = () => setShowInspector(prev => !prev);
    const onOpen = () => setShowInspector(true);
    const onClose = () => setShowInspector(false);
    window.addEventListener('stateInspector:toggle', onToggle);
    window.addEventListener('stateInspector:open', onOpen);
    window.addEventListener('stateInspector:close', onClose);
    return () => {
      window.removeEventListener('stateInspector:toggle', onToggle);
      window.removeEventListener('stateInspector:open', onOpen);
      window.removeEventListener('stateInspector:close', onClose);
    };
  }, []);

  // Global cleanup: Reset body/html styles on route change to restore scrolling
  // This ensures that if any component fails to cleanup (TimeframeScrollSelector, TickerScrollSelector, etc.),
  // navigation will restore normal scrolling behavior
  useEffect(() => {
    // Reset body styles that might prevent scrolling
    if (document.body.style.position === 'fixed') {
      document.body.style.position = '';
    }
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
    if (document.body.style.width === '100%') {
      document.body.style.width = '';
    }
    if (document.body.style.top) {
      document.body.style.top = '';
    }
  }, [location.pathname]);

  return (
    <PusherProvider>
      <QueryClientProvider client={queryClient}>
        <BannerContextProvider>
            <WebSocketProvider>
            <EnforcementAlertsProvider>
            <ThemeProvider>
              <TradingContextProvider>
                <TradingHoursRestrictionProvider>
                <TradingRulesConfigProvider>
                  <AppStateContextProvider>
                  <JournalModalProvider>
                    <OrderUndoProvider>
                      <StreamsContextProvider>
                        <BacktestProvider>
                        <TradeChecklistProvider>
                        <PositionCloseProvider>
                          <StdDevProvider>
                              <MaintenanceProvider>
                                <TradeConfirmationProvider>
                                  <MaintenanceGate>
                                    {/* WS handlers only outside /admin (Stream Monitor stays view-only) and /onboarding (no TS credentials yet). No GlobalStreamsManager — HTTP account streams removed. */}
                                    {!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/onboarding') && !location.pathname.startsWith('/content-creator/video/capture-window') && (
                                      <>
                                        <ChartsWebSocketHandler />
                                        <TradingWebSocketHandler />
                                        <QuotesWebSocketHandler />
                                        <StreamConnectionMonitor />
                                      </>
                                    )}
                                    <div className="text-light" style={{ height: '100dvh', paddingTop: 'env(safe-area-inset-top)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                      <ImpersonationBanner />
                                      <InstallAppBanner />
                                      <ConnectTradeCallbackHandler />
                                      <PendingDailyLossRuleApplier />
                                      <TradeStationReferralPromoBar />
                                      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                        <BannerWrapper/>
                                        <PriceAlertListener />
                                        <PositionLossListener />
                                        <AiOrderDraftListener />
                                        <AutoCloseConfirmModal />
                                        {showInspector && (
                                          <StateInspector onClose={() => setShowInspector(false)} />
                                        )}
                                      </div>
                                      <TradeStationReferralPromoFooter variant="authenticated" />
                                    </div>
                                  </MaintenanceGate>
                                </TradeConfirmationProvider>
                              </MaintenanceProvider>
                          </StdDevProvider>
                        </PositionCloseProvider>
                        </TradeChecklistProvider>
                        </BacktestProvider>
                      </StreamsContextProvider>
                    </OrderUndoProvider>
                </JournalModalProvider>
              </AppStateContextProvider>
                </TradingRulesConfigProvider>
                </TradingHoursRestrictionProvider>
              </TradingContextProvider>
            </ThemeProvider>
            </EnforcementAlertsProvider>
            </WebSocketProvider>
        </BannerContextProvider>
        {/* <ReactQueryDevtools initialIsOpen={false} position="bottom" /> */}
      </QueryClientProvider>
    </PusherProvider>
  )
}